import { createFileRoute } from "@tanstack/react-router";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { RelatedKnowledgeAuto } from "@/components/site/RelatedKnowledgeAuto";
import { breadcrumbs, faqPage, serviceSchema, speakable, aeoGeo } from "@/lib/seo/schema";

const FAQ: LandingFAQ[] = [
  {
    q: "Vad är konceptutveckling för film?",
    a: "Konceptutveckling är arbetet med att hitta den bärande kreativa idén som gör budskapet värt att titta på — före manus och produktion. Resultatet är ett konceptunderlag med idé, ton, visuell riktning och hur idén kan bära flera filmer över tid.",
  },
  {
    q: "Vad är skillnaden mellan koncept och manus?",
    a: "Konceptet är idén — mekanismen som gör filmen intressant. Manuset är utförandet: scener, repliker och speakertext. Ett bra koncept kan bära tio manus; ett bra manus utan koncept bär bara en film.",
  },
  {
    q: "Vad kostar konceptutveckling?",
    a: "Ett koncept för en enskild film: 20 000–40 000 kr. En kampanjplattform som ska bära flera filmer och kanaler över ett år: från 60 000 kr. Vid Prime-produktion ingår konceptarbetet i offerten.",
  },
  {
    q: "Hur många idéer presenterar ni?",
    a: "Två till tre. Fler idéspår låter generöst men gör beslutet svårare och tunnar ut arbetet per idé. Varje spår presenteras med hook, bärande grepp, visuell riktning, uppskattad produktionskostnad och tidplan.",
  },
  {
    q: "Kan ni utveckla koncept utan att producera filmen?",
    a: "Ja. Vi tar fristående konceptuppdrag och lämnar över ett underlag som vilket produktionsbolag som helst kan spela in.",
  },
];

const TITLE = "Konceptutveckling för film och kampanj | Fenny";
const DESCRIPTION =
  "Konceptutveckling för reklamfilm och kampanj: bärande idé, ton, visuell riktning och kampanjplattform. Två till tre idéspår med pris och tidplan.";

export const Route = createFileRoute("/konceptutveckling-film")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Konceptutveckling för film — Fenny" },
      { property: "og:description", content: "Från budskap till bärande idé — koncept som håller för fler än en film." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/konceptutveckling-film" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/konceptutveckling-film" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({ path: "/konceptutveckling-film", name: TITLE, description: DESCRIPTION })) },
      { type: "application/ld+json", children: JSON.stringify(speakable([".lede"])) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceSchema({
            name: "Konceptutveckling för film",
            path: "/konceptutveckling-film",
            serviceType: "Konceptutveckling",
            description: "Kreativ konceptutveckling och kampanjplattform för reklamfilm och varumärkesfilm.",
            priceRange: "20 000–120 000 kr",
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Manus och koncept", path: "/manus-och-koncept" },
            { name: "Konceptutveckling", path: "/konceptutveckling-film" },
          ]),
        ),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <LandingPage
        eyebrow="Konceptutveckling"
        title={<>Ett koncept som bär mer än en film.</>}
        lede="Konceptutveckling är arbetet mellan brief och manus: att hitta den idé som gör budskapet värt att titta på, och som håller för flera filmer, kanaler och kvartal. Vi presenterar två till tre spår — alla prissatta och tidsatta, så valet blir ett affärsbeslut."
        tldr={
          <p>
            Konceptutveckling för film innebär att ta fram en bärande kreativ idé
            med ton, visuell riktning och bärkraft över tid — före manus. Pris i
            Sverige 2026: <strong>20 000–40 000 kr</strong> för en enskild film,{" "}
            <strong>från 60 000 kr</strong> för en kampanjplattform. Fenny
            presenterar alltid <strong>2–3 idéspår</strong> med pris och tidplan
            per spår.
          </p>
        }
        ctaPrimary={{ label: "Boka strategimöte", to: "/kontakt" }}
        ctaSecondary={{ label: "Manus och koncept", to: "/manus-och-koncept" }}
        trackingId="konceptutveckling-film"
        bullets={[
          { title: "Idén före kameran", body: "Produktionsvärde räddar inte en film som saknar anledning att finnas." },
          { title: "Prissatta spår", body: "Varje idé kommer med kostnad och tidplan så ni kan välja med öppna ögon." },
          { title: "Byggd för återanvändning", body: "Konceptet ska bära nästa film också — inte bara den ni beställer nu." },
        ]}
        pricing={{
          heading: "Vad kostar konceptutveckling?",
          intro: "Riktpriser exklusive moms, Sverige 2026.",
          rows: [
            { format: "Koncept enskild film", scope: "2–3 idéspår, valt spår utvecklas", price: "20 000–40 000 kr", lead: "2 veckor" },
            { format: "Kampanjplattform", scope: "Bärande idé, tonalitet, filmfamilj, kanalapplikation", price: "40 000–120 000 kr", lead: "3–5 veckor" },
            { format: "Konceptworkshop", scope: "Halvdag med ert team, dokumenterad riktning", price: "18 000 kr", lead: "1 vecka" },
            { format: "Koncept i abonnemang", scope: "Löpande idéarbete och innehållsplan", price: "ingår från 14 995 kr/mån", lead: "löpande" },
          ],
        }}
        process={{
          heading: "Så tas ett koncept fram",
          steps: [
            { label: "Insikt", body: "Vad vet vi om målgruppen som konkurrenterna inte utnyttjar? En mening, inte en rapport.", duration: "vecka 1" },
            { label: "Idégenerering", body: "Brett först, sedan hårt gallrat. Vi testar idéerna mot budget innan de visas.", duration: "vecka 1" },
            { label: "Presentation", body: "2–3 spår med hook, visuell riktning, referenser, pris och tidplan.", duration: "vecka 2" },
            { label: "Val och skärpning", body: "Valt spår vässas och skrivs ut som konceptunderlag.", duration: "vecka 2–3" },
            { label: "Överlämning till manus", body: "Konceptet blir manus och eventuellt storyboard.", duration: "vecka 3" },
          ],
        }}
        sections={[
          {
            id: "vad-ar-ett-bra-koncept",
            heading: "Vad som skiljer ett bärande koncept från en trevlig idé",
            body: (
              <>
                <p>
                  Ett bärande koncept kan sammanfattas i en mening, går att
                  producera inom budget, tål att upprepas utan att bli tråkigt och
                  fungerar även i 9:16 utan ljud. Klarar idén inte alla fyra
                  testerna är den en trevlig idé — inte ett koncept.
                </p>
                <p>
                  Vi testar också det obekväma: skulle konkurrenten kunna sätta
                  sin logotyp i slutet och filmen fortfarande fungera? Om ja
                  saknar konceptet varumärkeskoppling.
                </p>
              </>
            ),
          },
          {
            id: "nasta-steg",
            heading: "Från koncept till färdig film",
            body: (
              <p>
                Konceptet blir{" "}
                <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/reklamfilmsmanus">reklamfilmsmanus</a>,
                sedan{" "}
                <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/storyboard">storyboard</a>{" "}
                och{" "}
                <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/forproduktion">förproduktion</a>{" "}
                innan inspelning. Vill ni att samma koncept ska bära löpande
                innehåll passar{" "}
                <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/pulse">Pulse-abonnemang</a>.
              </p>
            ),
          },
        ]}
        faq={FAQ}
        caseLimit={4}
        riskReducers
        closing={{
          title: "Har ni ett budskap men ingen idé?",
          body: "Boka 30 minuter så testar vi om det finns ett koncept i det.",
          primary: { label: "Boka strategimöte", to: "/kontakt" },
        }}
      />
      <RelatedKnowledgeAuto topics={["manus", "strategi", "reklamfilm", "kampanjfilm"]} />
    </>
  );
}
